import React, { useEffect, useMemo, useRef, useState } from "react";
import "./PaymentReportsPage.css";
import { getReportPeriod } from "../reportPeriod";
import {
    createPaymentIncomingClassificationRule,
    createPaymentOutgoingClassificationRule,
    getPaymentCounterpartyMappings,
    getPaymentIncomingClassificationRules,
    getPaymentOutgoingClassificationRules,
    getPaymentImportRuns,
    getPaymentManagementSummary,
    getPaymentsReport,
    getPaymentSummary,
    getPaymentUnmappedCounterparties,
    getPaymentUnverifiedEntities,
    importSalesDrivePayments,
    recalculatePaymentReport,
    downloadPaymentsExport,
    updatePaymentIncomingClassificationRule,
    updatePaymentOutgoingClassificationRule,
    upsertAccountBalanceAdjustment,
} from "../api/paymentReportsApi";
import { getDropshipEnterprises } from "../api/dropshipEnterpriseApi";
import { createCounterparty, upsertCounterpartyDropshipLink } from "../api/counterpartiesApi";
import { getBusinessOrganizations } from "../api/businessOrganizationsApi";
import {
    confirmNovaPayDraft,
    deleteNovaPayDraft,
    downloadNovaPayExport,
    exportNovaPayDrafts,
    getNovaPayDrafts,
    updateNovaPayDraft,
    uploadPediakidInvoice,
} from "../api/novapayImportApi";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "18px",
    maxWidth: "1440px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const categoryLabels = {
    customer_receipt: "Клиентские поступления",
    taxable_agency_receipt: "Агентские поступления / облагаемые",
    excluded_receipt: "Исключенные поступления",
    other_receipt: "Прочие поступления / возвраты",
    internal_transfer: "Внутренние переводы",
    unknown_incoming: "Неизвестные входящие",
    supplier_payment: "Оплаты поставщикам",
    tax_payment: "Налоги",
    owner_withdrawal: "Личные перебросы",
    logistics_expense: "Логистика / Нова Пошта",
    platform_fee: "Платформы (Prom / Tabletki / Rozetka)",
    telephony_expense: "Телефония / связь",
    refund_to_customer: "Возврат покупателю",
    rent_expense: "Аренда помещения",
    other_expense: "Прочие расходы",
    unknown_outgoing: "Неизвестные исходящие",
};

const balanceSourceLabels = {
    manual_current_period: "Введено за период",
    carried_forward: "Перенесено",
    balance_checkpoint: "От факта на дату",
};

const qualityLabels = {
    unmapped_outgoing: "Unmapped outgoing",
    unknown_incoming: "Unknown incoming",
    payments_without_entity: "Без предприятия",
    payments_without_account: "Без счета",
    payments_without_counterparty: "Без контрагента",
    direct_internal_without_pair: "Внутренние без пары",
    unverified_entities: "Непроверенные предприятия",
};

const today = new Date();
const { periodFrom: defaultPeriodFrom, periodTo: defaultPeriodTo } = getReportPeriod("current_month", today);

const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return String(value);
    }
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumAmount = (rows, key = "amount") =>
    rows.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0).toFixed(2);

const kpiToneClass = {
    good: "tone-good",
    warn: "tone-warn",
    bad: "tone-bad",
    blue: "tone-blue",
    default: "",
};

const KpiCard = ({ label, amount, count, countLabel = "платежей", tone = "default", onClick, active = false, valueText }) => {
    const clickable = typeof onClick === "function";
    const classes = ["pr-kpi", kpiToneClass[tone] || "", clickable ? "clickable" : "", active ? "active" : ""]
        .filter(Boolean)
        .join(" ");
    return (
        <div
            className={classes}
            onClick={onClick}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
        >
            <span className="lbl">{label}</span>
            <span className="val">{valueText !== undefined ? valueText : formatAmount(amount)}</span>
            {count !== undefined && <span className="cnt">{count} {countLabel}</span>}
        </div>
    );
};

const PaymentReportsPage = ({
    embedded = false,
    initialTab = "summary",
    allowedTabs = null,
    title = "Платежная отчетность",
    subtitle = "Управленческая сводка по платежам, счетам, поставщикам и качеству данных.",
}) => {
    const [periodFrom, setPeriodFrom] = useState(defaultPeriodFrom);
    const [periodTo, setPeriodTo] = useState(defaultPeriodTo);
    const [periodPreset, setPeriodPreset] = useState("current_month");
    const [organizations, setOrganizations] = useState([]);
    const [businessEntityId, setBusinessEntityId] = useState("");
    const [businessAccountId, setBusinessAccountId] = useState("");
    const [management, setManagement] = useState(null);
    const [paymentsReport, setPaymentsReport] = useState(null);
    const [summary, setSummary] = useState(null);
    const [imports, setImports] = useState([]);
    const [unmapped, setUnmapped] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [incomingRules, setIncomingRules] = useState([]);
    const [outgoingRules, setOutgoingRules] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState("");
    const [error, setError] = useState("");
    const [balanceDrafts, setBalanceDrafts] = useState({});
    const [mappingDrafts, setMappingDrafts] = useState({});
    const [incomingRuleDrafts, setIncomingRuleDrafts] = useState({});
    const [outgoingRuleDrafts, setOutgoingRuleDrafts] = useState({});
    const [selectedQualityIssue, setSelectedQualityIssue] = useState(null);
    const [qualityDetailRows, setQualityDetailRows] = useState([]);
    const [qualityDetailLoading, setQualityDetailLoading] = useState(false);

    const applyPeriodPreset = (preset) => {
        setPeriodPreset(preset);
        if (preset === "custom") return;
        const range = getReportPeriod(preset);
        setPeriodFrom(range.periodFrom);
        setPeriodTo(range.periodTo);
    };

    const loadReports = async ({ rangeFrom = periodFrom, rangeTo = periodTo } = {}) => {
        setLoading(true);
        setError("");
        setSelectedQualityIssue(null);
        setQualityDetailRows([]);
        try {
            const [managementData, paymentsData, summaryData, importsData, unmappedData, mappingsData, incomingRulesData, outgoingRulesData, suppliersData] = await Promise.all([
                getPaymentManagementSummary({ periodFrom: rangeFrom, periodTo: rangeTo, businessEntityId, businessAccountId }),
                getPaymentsReport({ periodFrom: rangeFrom, periodTo: rangeTo, limit: 5000, businessEntityId, businessAccountId }),
                getPaymentSummary({ periodFrom: rangeFrom, periodTo: rangeTo, businessEntityId, businessAccountId }),
                getPaymentImportRuns({ limit: 8 }),
                getPaymentUnmappedCounterparties({ periodFrom: rangeFrom, periodTo: rangeTo, limit: 100, examples: 2 }),
                getPaymentCounterpartyMappings({ limit: 500 }),
                getPaymentIncomingClassificationRules({ limit: 500 }),
                getPaymentOutgoingClassificationRules({ limit: 500 }),
                getDropshipEnterprises(),
            ]);
            setManagement(managementData);
            setPaymentsReport(paymentsData);
            setSummary(summaryData);
            setImports(importsData);
            setUnmapped(unmappedData?.groups || []);
            setMappings(mappingsData || []);
            setIncomingRules(incomingRulesData || []);
            setOutgoingRules(outgoingRulesData || []);
            setSuppliers(suppliersData || []);
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось загрузить отчеты.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scopeMounted = useRef(false);
    useEffect(() => {
        if (!scopeMounted.current) {
            scopeMounted.current = true;
            return;
        }
        loadReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessEntityId, businessAccountId]);

    useEffect(() => {
        const loadOrganizations = async () => {
            try {
                setOrganizations(await getBusinessOrganizations());
            } catch (err) {
                setError(err?.response?.data?.detail || err.message || "Не удалось загрузить юрлица.");
            }
        };
        loadOrganizations();
    }, []);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    const runImport = async () => {
        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            const result = await importSalesDrivePayments({ periodFrom, periodTo, paymentType: "all" });
            setActionMessage(`Импорт: incoming=${result.incoming_count}, outcoming=${result.outcoming_count}, created=${result.created_count}, updated=${result.updated_count}`);
            await recalculatePaymentReport({ periodFrom, periodTo });
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Импорт не выполнен.");
            setLoading(false);
        }
    };

    const runRecalculate = async () => {
        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            const result = await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage(`Пересчет: всего=${result.total_payments}, mapped=${result.supplier_mapped}, unmapped=${result.supplier_unmapped}`);
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Пересчет не выполнен.");
            setLoading(false);
        }
    };

    const runMonthlyReconciliation = async () => {
        const [year, month] = periodFrom.slice(0, 7).split("-").map(Number);
        const monthStart = `${year}-${String(month).padStart(2, "0")}-01`;
        const monthEnd = `${year}-${String(month).padStart(2, "0")}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            const result = await importSalesDrivePayments({ periodFrom: monthStart, periodTo: monthEnd, paymentType: "all" });
            const recalculation = await recalculatePaymentReport({ periodFrom: monthStart, periodTo: monthEnd });
            setPeriodFrom(monthStart);
            setPeriodTo(monthEnd);
            setPeriodPreset("custom");
            setActionMessage(
                `Сверка ${monthStart.slice(0, 7)}: incoming=${result.incoming_count}, outcoming=${result.outcoming_count}, `
                + `created=${result.created_count}, updated=${result.updated_count}, пересчитано=${recalculation.total_payments}`,
            );
            await loadReports({ rangeFrom: monthStart, rangeTo: monthEnd });
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Сверка месяца не выполнена.");
            setLoading(false);
        }
    };

    const saveAccountBalance = async (accountId) => {
        const draft = balanceDrafts[accountId] || {};
        setLoading(true);
        setError("");
        try {
            await upsertAccountBalanceAdjustment({
                account_id: accountId,
                balance_date: draft.balance_date || periodTo,
                actual_balance: draft.actual_balance || null,
                opening_balance_adjustment: "0",
                closing_balance_adjustment: "0",
                comment: draft.comment || null,
                created_by: localStorage.getItem("user_login") || "admin-panel",
            });
            setActionMessage("Фактический остаток счета сохранен.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось сохранить остатки.");
            setLoading(false);
        }
    };

    const saveAccountOpeningBalance = async (accountId) => {
        const draft = balanceDrafts[accountId] || {};
        const openingPeriodMonth = draft.opening_period_month || `${periodFrom.slice(0, 7)}-01`;
        setLoading(true);
        setError("");
        try {
            await upsertAccountBalanceAdjustment({
                account_id: accountId,
                period_month: openingPeriodMonth,
                balance_date: null,
                actual_balance: null,
                actual_opening_balance: draft.opening_balance || "0",
                opening_balance_adjustment: "0",
                closing_balance_adjustment: "0",
                comment: draft.opening_comment || draft.comment || null,
                created_by: localStorage.getItem("user_login") || "admin-panel",
            });
            setActionMessage("Начальный остаток месяца сохранен.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось сохранить начальный остаток.");
            setLoading(false);
        }
    };

    const createMapping = async (mappingKey, source = {}) => {
        const draft = mappingDrafts[mappingKey] || {};
        const supplierCode = draft.supplier_code || "";
        const matchType = draft.match_type || (source.counterparty_tax_id ? "tax_id" : "exact");
        const pattern = draft.counterparty_pattern ?? source.counterparty_name ?? "";
        const taxId = draft.counterparty_tax_id ?? source.counterparty_tax_id ?? (matchType === "tax_id" ? pattern : "");

        const legalName = String(pattern || source.counterparty_name || taxId || "").trim();
        if (!legalName) {
            setError("Не удалось определить название контрагента — заполните паттерн или ЕДРПОУ перед связыванием.");
            return;
        }

        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            const counterparty = await createCounterparty({
                legal_name: legalName,
                short_name: String(source.counterparty_name || pattern || "").trim() || null,
                tax_id: String(taxId || "").trim() || null,
                counterparty_type: "tov",
                vat_payer: false,
                vat_enabled: false,
                is_active: true,
                notes: draft.notes || "Created from payment reports unmapped counterparty.",
            });
            await upsertCounterpartyDropshipLink(counterparty.id, {
                supplier_code: supplierCode,
                is_default: true,
                is_active: true,
                priority: Number(draft.priority || 100),
            });
            await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage("Контрагент связан с отправителем, период пересчитан.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось связать контрагента с отправителем.");
            setLoading(false);
        }
    };

    const createIncomingRule = async (ruleKey, source = {}) => {
        const draft = incomingRuleDrafts[ruleKey] || {};
        const matchType = draft.match_type || "contains";
        const fieldScope = draft.field_scope || "purpose";
        const pattern = draft.pattern ?? source.purpose ?? "";
        const taxId = draft.counterparty_tax_id ?? source.counterparty_tax_id ?? "";

        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            await createPaymentIncomingClassificationRule({
                target_category: draft.target_category || "customer_receipt",
                match_type: matchType,
                field_scope: fieldScope,
                pattern,
                counterparty_tax_id: taxId,
                priority: Number(draft.priority || 100),
                is_active: true,
                notes: draft.notes || "Created from payment reports incoming classification.",
                created_by: localStorage.getItem("user_login") || "admin-panel",
                updated_by: localStorage.getItem("user_login") || "admin-panel",
            });
            await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage("Правило входящих создано, период пересчитан.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось создать правило входящего платежа.");
            setLoading(false);
        }
    };

    const toggleIncomingRule = async (rule) => {
        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            await updatePaymentIncomingClassificationRule(rule.id, {
                is_active: !rule.is_active,
                updated_by: localStorage.getItem("user_login") || "admin-panel",
            });
            await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage("Правило входящих обновлено, период пересчитан.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось обновить правило входящего платежа.");
            setLoading(false);
        }
    };

    const createOutgoingRule = async (ruleKey, source = {}) => {
        const draft = outgoingRuleDrafts[ruleKey] || {};
        const matchType = draft.match_type || "contains";
        const fieldScope = draft.field_scope || "purpose";
        const pattern = matchType === "tax_id" ? "" : draft.pattern ?? source.purpose ?? "";
        const taxId = draft.counterparty_tax_id ?? source.counterparty_tax_id ?? "";

        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            await createPaymentOutgoingClassificationRule({
                target_category: draft.target_category || "owner_withdrawal",
                match_type: matchType,
                field_scope: matchType === "tax_id" ? "tax_id" : fieldScope,
                pattern,
                counterparty_tax_id: taxId,
                priority: Number(draft.priority || 100),
                is_active: true,
                notes: draft.notes || "Created from payment reports outgoing classification.",
                created_by: localStorage.getItem("user_login") || "admin-panel",
                updated_by: localStorage.getItem("user_login") || "admin-panel",
            });
            await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage("Правило исходящих создано, период пересчитан.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось создать правило исходящего платежа.");
            setLoading(false);
        }
    };

    const updateOutgoingRule = async (rule, ruleKey) => {
        const draft = outgoingRuleDrafts[ruleKey] || {};
        const matchType = draft.match_type || rule.match_type;
        const pattern = matchType === "tax_id" ? null : (draft.pattern ?? rule.pattern ?? "");
        try {
            setLoading(true);
            setError("");
            setActionMessage("");
            await updatePaymentOutgoingClassificationRule(rule.id, {
                target_category: draft.target_category ?? rule.target_category,
                match_type: matchType,
                field_scope: matchType === "tax_id" ? "tax_id" : (draft.field_scope ?? rule.field_scope),
                pattern,
                counterparty_tax_id: draft.counterparty_tax_id ?? rule.counterparty_tax_id,
                priority: Number(draft.priority ?? rule.priority),
                updated_by: localStorage.getItem("user_login") || "admin-panel",
            });
            await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage("Правило исходящих обновлено, период пересчитан.");
            await loadReports();
            return true;
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось изменить правило исходящего платежа.");
            setLoading(false);
            return false;
        }
    };

    const toggleOutgoingRule = async (rule) => {
        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            await updatePaymentOutgoingClassificationRule(rule.id, {
                is_active: !rule.is_active,
                updated_by: localStorage.getItem("user_login") || "admin-panel",
            });
            await recalculatePaymentReport({ periodFrom, periodTo });
            setActionMessage("Правило исходящих обновлено, период пересчитан.");
            await loadReports();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось обновить правило исходящего платежа.");
            setLoading(false);
        }
    };

    const selectQualityIssue = async (key) => {
        if (selectedQualityIssue === key) {
            setSelectedQualityIssue(null);
            setQualityDetailRows([]);
            return;
        }
        setSelectedQualityIssue(key);
        setQualityDetailRows([]);
        setQualityDetailLoading(true);
        setError("");
        try {
            if (key === "unverified_entities") {
                const data = await getPaymentUnverifiedEntities();
                setQualityDetailRows(data?.entities || []);
            } else {
                const data = await getPaymentsReport({ periodFrom, periodTo, qualityIssue: key, limit: 5000, businessEntityId, businessAccountId });
                setQualityDetailRows(data?.payments || []);
            }
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось загрузить детализацию.");
            setSelectedQualityIssue(null);
        } finally {
            setQualityDetailLoading(false);
        }
    };

    const availableAccounts = useMemo(() => {
        const organization = organizations.find((item) => String(item.id) === String(businessEntityId));
        return organization?.accounts || [];
    }, [organizations, businessEntityId]);

    const selectBusinessEntity = (nextBusinessEntityId) => {
        setBusinessEntityId(nextBusinessEntityId);
        const organization = organizations.find((item) => String(item.id) === String(nextBusinessEntityId));
        if (!organization?.accounts?.some((account) => String(account.id) === String(businessAccountId))) {
            setBusinessAccountId("");
        }
    };

    const runExport = async () => {
        setLoading(true);
        setError("");
        setActionMessage("");
        try {
            await downloadPaymentsExport({ periodFrom, periodTo, businessEntityId, businessAccountId });
            setActionMessage("Экспорт платежей скачан.");
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось экспортировать платежи.");
        } finally {
            setLoading(false);
        }
    };

    const scopeLabel = useMemo(() => {
        if (!businessEntityId) return "Все юрлица";
        const organization = organizations.find((item) => String(item.id) === String(businessEntityId));
        const account = (organization?.accounts || []).find((item) => String(item.id) === String(businessAccountId));
        const entityName = organization?.short_name || `Юрлицо #${businessEntityId}`;
        return account ? `${entityName} · ${account.label || account.account_number}` : entityName;
    }, [organizations, businessEntityId, businessAccountId]);
    const incomingByCategory = useMemo(() => summary?.incoming_by_category || [], [summary]);
    const outgoingByCategory = useMemo(() => summary?.outgoing_by_category || [], [summary]);
    const supplierPayments = useMemo(() => management?.supplier_payments_by_entity || [], [management]);
    const accountMovements = useMemo(() => management?.account_movements || [], [management]);
    const paymentRows = useMemo(() => paymentsReport?.payments || [], [paymentsReport]);
    const incomingPayments = useMemo(() => paymentRows.filter((payment) => payment.payment_type === "incoming"), [paymentRows]);
    const outgoingPayments = useMemo(() => paymentRows.filter((payment) => payment.payment_type === "outcoming"), [paymentRows]);
    const unknownIncomingPayments = useMemo(() => incomingPayments.filter((payment) => payment.category === "unknown_incoming"), [incomingPayments]);
    const unknownOutgoingPayments = useMemo(() => outgoingPayments.filter((payment) => payment.category === "unknown_outgoing" || payment.mapping_status === "unmapped"), [outgoingPayments]);
    const dataQuality = management?.data_quality || {};

    const kpis = useMemo(() => {
        const byIncoming = Object.fromEntries(incomingByCategory.map((item) => [item.category, item]));
        const byOutgoing = Object.fromEntries(outgoingByCategory.map((item) => [item.category, item]));
        const externalIncoming = Number(summary?.incoming_total?.amount || 0) - Number(byIncoming.internal_transfer?.amount || 0);
        const externalOutgoing = Number(summary?.outcoming_total?.amount || 0) - Number(byOutgoing.internal_transfer?.amount || 0);
        return {
            customer: byIncoming.customer_receipt || { count: 0, amount: "0" },
            agency: byIncoming.taxable_agency_receipt || { count: 0, amount: "0" },
            suppliers: byOutgoing.supplier_payment || { count: 0, amount: "0" },
            taxes: byOutgoing.tax_payment || { count: 0, amount: "0" },
            owner: byOutgoing.owner_withdrawal || { count: 0, amount: "0" },
            netExternal: (externalIncoming - externalOutgoing).toFixed(2),
        };
    }, [incomingByCategory, outgoingByCategory, summary]);

    const incomingByEntity = useMemo(() => management?.incoming_by_category_entity_account || [], [management]);
    const outgoingByEntity = useMemo(() => management?.outgoing_by_category_entity_account || [], [management]);

    const enterpriseSummary = useMemo(() => {
        const byEntity = new Map();
        const ensure = (name) => {
            const key = name || "Без предприятия";
            if (!byEntity.has(key)) {
                byEntity.set(key, {
                    name: key,
                    customer: 0,
                    agency: 0,
                    refundsOther: 0,
                    excludedIncoming: 0,
                    unknownIncoming: 0,
                    suppliers: 0,
                    platforms: 0,
                    taxes: 0,
                    owner: 0,
                    logistics: 0,
                    telephony: 0,
                    rent: 0,
                    otherExpenses: 0,
                    customerRefunds: 0,
                    unknownOutgoing: 0,
                    inTotal: 0,
                    inInternal: 0,
                    outTotal: 0,
                    outInternal: 0,
                });
            }
            return byEntity.get(key);
        };
        incomingByEntity.forEach((row) => {
            const entry = ensure(row.business_entity_name);
            const amount = Number(row.amount) || 0;
            entry.inTotal += amount;
            if (row.category === "customer_receipt") entry.customer += amount;
            if (row.category === "taxable_agency_receipt") entry.agency += amount;
            if (row.category === "other_receipt") entry.refundsOther += amount;
            if (row.category === "excluded_receipt") entry.excludedIncoming += amount;
            if (row.category === "unknown_incoming") entry.unknownIncoming += amount;
            if (row.category === "internal_transfer") entry.inInternal += amount;
        });
        outgoingByEntity.forEach((row) => {
            const entry = ensure(row.business_entity_name);
            const amount = Number(row.amount) || 0;
            entry.outTotal += amount;
            if (row.category === "supplier_payment") entry.suppliers += amount;
            if (row.category === "platform_fee") entry.platforms += amount;
            if (row.category === "tax_payment") entry.taxes += amount;
            if (row.category === "owner_withdrawal") entry.owner += amount;
            if (row.category === "logistics_expense") entry.logistics += amount;
            if (row.category === "telephony_expense") entry.telephony += amount;
            if (row.category === "rent_expense") entry.rent += amount;
            if (row.category === "other_expense") entry.otherExpenses += amount;
            if (row.category === "refund_to_customer") entry.customerRefunds += amount;
            if (row.category === "unknown_outgoing") entry.unknownOutgoing += amount;
            if (row.category === "internal_transfer") entry.outInternal += amount;
        });
        const rows = [...byEntity.values()]
            .map((entry) => ({
                ...entry,
                incoming: entry.inTotal - entry.inInternal,
                outgoing: entry.outTotal - entry.outInternal,
            }))
            .sort((left, right) => left.name.localeCompare(right.name, "ru"));
        const totals = rows.reduce(
            (acc, row) => ({
                customer: acc.customer + row.customer,
                agency: acc.agency + row.agency,
                refundsOther: acc.refundsOther + row.refundsOther,
                excludedIncoming: acc.excludedIncoming + row.excludedIncoming,
                unknownIncoming: acc.unknownIncoming + row.unknownIncoming,
                suppliers: acc.suppliers + row.suppliers,
                platforms: acc.platforms + row.platforms,
                taxes: acc.taxes + row.taxes,
                owner: acc.owner + row.owner,
                logistics: acc.logistics + row.logistics,
                telephony: acc.telephony + row.telephony,
                rent: acc.rent + row.rent,
                otherExpenses: acc.otherExpenses + row.otherExpenses,
                unknownOutgoing: acc.unknownOutgoing + row.unknownOutgoing,
                incoming: acc.incoming + row.incoming,
                outgoing: acc.outgoing + row.outgoing,
            }),
            {
                customer: 0, agency: 0, refundsOther: 0, excludedIncoming: 0, unknownIncoming: 0,
                suppliers: 0, platforms: 0, taxes: 0, owner: 0, logistics: 0, telephony: 0, rent: 0, otherExpenses: 0, unknownOutgoing: 0,
                incoming: 0, outgoing: 0,
            },
        );
        return { rows, totals };
    }, [incomingByEntity, outgoingByEntity]);

    const tabs = [
        ["summary", "Сводка"],
        ["enterprises", "Предприятия"],
        ["accounts", "Счета"],
        ["incoming", "Входящие"],
        ["outgoing", "Исходящие"],
        ["suppliers", "Поставщики"],
        ["mapping", "Мапинг"],
        ["quality", "Контроль"],
        ["imports", "Импорты"],
        ["novapay", "NovaPay импорт"],
    ].filter(([key]) => !allowedTabs || allowedTabs.includes(key));

    const attention = {
        mapping: unmapped.length > 0 || unknownIncomingPayments.length > 0 || unknownOutgoingPayments.length > 0,
        quality: Object.values(dataQuality || {}).some((item) => Number(item?.count || 0) > 0),
    };

    return (
        <div className="payments-report" style={embedded ? { ...pageStyle, padding: 0, maxWidth: "none" } : pageStyle}>
            <section className="pr-card pr-toolbar">
                <div className="pr-toolbar-head">
                    <div className="pr-head">
                        <h1>{title}</h1>
                        <p>{subtitle}</p>
                    </div>
                    <div className="pr-toolbar-actions">
                        <div className="pr-fld">
                            <label>Юрлицо</label>
                            <select className="pr-inp" value={businessEntityId} onChange={(event) => selectBusinessEntity(event.target.value)}>
                                <option value="">Все юрлица</option>
                                {organizations.map((organization) => (
                                    <option key={organization.id} value={organization.id}>{organization.short_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="pr-fld">
                            <label>Счёт</label>
                            <select
                                className="pr-inp"
                                value={businessAccountId}
                                onChange={(event) => setBusinessAccountId(event.target.value)}
                                disabled={!businessEntityId}
                            >
                                <option value="">Все счета</option>
                                {availableAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                        {account.label || account.account_number} {account.account_number && account.label ? `(${account.account_number})` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="pr-fld">
                            <label>Период</label>
                            <select className="pr-inp" value={periodPreset} onChange={(event) => applyPeriodPreset(event.target.value)}>
                                <option value="current_month">Текущий месяц (по сегодня)</option>
                                <option value="previous_month">Предыдущий месяц</option>
                                <option value="custom">Произвольный период</option>
                            </select>
                        </div>
                        <div className="pr-fld">
                            <label>С</label>
                            <input className="pr-inp" type="date" value={periodFrom} onChange={(event) => { setPeriodFrom(event.target.value); setPeriodPreset("custom"); }} />
                        </div>
                        <div className="pr-fld">
                            <label>По</label>
                            <input className="pr-inp" type="date" value={periodTo} onChange={(event) => { setPeriodTo(event.target.value); setPeriodPreset("custom"); }} />
                        </div>
                        <button type="button" className="pr-btn pr-btn-ghost" onClick={loadReports} disabled={loading}>
                            Загрузить отчет
                        </button>
                        <button type="button" className="pr-btn pr-btn-ghost" onClick={runExport} disabled={loading}>
                            Экспорт XLSX
                        </button>
                        <button type="button" className="pr-btn pr-btn-ghost" onClick={runRecalculate} disabled={loading}>
                            Пересчитать
                        </button>
                        <button type="button" className="pr-btn pr-btn-ghost" onClick={runMonthlyReconciliation} disabled={loading}>
                            Сверить месяц
                        </button>
                        <button type="button" className="pr-btn pr-btn-primary" onClick={runImport} disabled={loading}>
                            Импорт из SalesDrive
                        </button>
                    </div>
                </div>
                {(loading || error || actionMessage) && (
                    <div className={`pr-note${error ? " err" : ""}`}>
                        <span className="dot" />
                        {loading ? "Загрузка..." : error || actionMessage}
                    </div>
                )}
            </section>

            {tabs.length > 1 && <div className="pr-subtabs">
                {tabs.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        className={`pr-subtab${activeTab === key ? " active" : ""}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                        {attention[key] && <span className="ndot" />}
                    </button>
                ))}
            </div>}

            {activeTab === "summary" && (
                <div className="pr-view">
                    <div className="pr-kpi-grid">
                        <KpiCard label="Клиентские поступления" amount={kpis.customer.amount} count={kpis.customer.count} tone="good" />
                        <KpiCard label="Агентские поступления" amount={kpis.agency.amount} count={kpis.agency.count} tone="good" />
                        <KpiCard label="Оплаты поставщикам" amount={kpis.suppliers.amount} count={kpis.suppliers.count} tone="blue" />
                        <KpiCard label="Налоги" amount={kpis.taxes.amount} count={kpis.taxes.count} tone="warn" />
                        <KpiCard label="Личные перебросы" amount={kpis.owner.amount} count={kpis.owner.count} />
                        <KpiCard label="Внешний cash flow" amount={kpis.netExternal} tone={Number(kpis.netExternal) >= 0 ? "good" : "bad"} />
                    </div>
                    <div className="pr-card">
                        <div className="pr-panel-head">
                            <h3>Сводка по категориям</h3>
                            <span className="pr-muted">{scopeLabel}</span>
                        </div>
                        <div className="pr-panel-pad pr-split2">
                            <CategoryTable title="Входящие" rows={incomingByCategory} />
                            <CategoryTable title="Исходящие" rows={outgoingByCategory} />
                        </div>
                    </div>
                </div>
            )}
            {activeTab === "enterprises" && (
                <div className="pr-view">
                    <EnterpriseBreakdown rows={enterpriseSummary.rows} totals={enterpriseSummary.totals} />
                </div>
            )}

            {activeTab === "accounts" && (
                <div className="pr-view">
                    <AccountsTable
                        rows={accountMovements}
                        drafts={balanceDrafts}
                        setDrafts={setBalanceDrafts}
                        onSave={saveAccountBalance}
                        onSaveOpening={saveAccountOpeningBalance}
                        loading={loading}
                        defaultBalanceDate={periodTo}
                        defaultPeriodMonth={`${periodFrom.slice(0, 7)}-01`}
                    />
                </div>
            )}
            {activeTab === "incoming" && (
                <div className="pr-view">
                    <PaymentDetailsTable
                        title="Входящие платежи"
                        rows={incomingPayments}
                        totalAmount={paymentsReport?.total?.incoming_amount}
                        unknownCount={unknownIncomingPayments.length}
                    />
                </div>
            )}
            {activeTab === "outgoing" && (
                <div className="pr-view">
                    <PaymentDetailsTable
                        title="Исходящие платежи"
                        rows={outgoingPayments}
                        totalAmount={paymentsReport?.total?.outcoming_amount}
                        unknownCount={unknownOutgoingPayments.length}
                    />
                </div>
            )}
            {activeTab === "suppliers" && <div className="pr-view"><SupplierTable rows={supplierPayments} /></div>}
            {activeTab === "mapping" && (
                <MappingPanel
                    unmapped={unmapped}
                    mappings={mappings}
                    incomingRules={incomingRules}
                    outgoingRules={outgoingRules}
                    unknownIncomingPayments={unknownIncomingPayments}
                    unknownOutgoingPayments={unknownOutgoingPayments}
                    suppliers={suppliers}
                    drafts={mappingDrafts}
                    setDrafts={setMappingDrafts}
                    incomingDrafts={incomingRuleDrafts}
                    setIncomingDrafts={setIncomingRuleDrafts}
                    outgoingDrafts={outgoingRuleDrafts}
                    setOutgoingDrafts={setOutgoingRuleDrafts}
                    onCreate={createMapping}
                    onCreateIncomingRule={createIncomingRule}
                    onToggleIncomingRule={toggleIncomingRule}
                    onCreateOutgoingRule={createOutgoingRule}
                    onToggleOutgoingRule={toggleOutgoingRule}
                    onUpdateOutgoingRule={updateOutgoingRule}
                    loading={loading}
                />
            )}
            {activeTab === "quality" && (
                <div className="pr-view">
                    <QualityPanel
                        quality={dataQuality}
                        selectedKey={selectedQualityIssue}
                        onSelect={selectQualityIssue}
                        detailRows={qualityDetailRows}
                        detailLoading={qualityDetailLoading}
                    />
                </div>
            )}
            {activeTab === "imports" && <div className="pr-view"><ImportsTable rows={imports} /></div>}
            {activeTab === "novapay" && <div className="pr-view"><NovaPayImportPanel /></div>}
        </div>
    );
};

const unknownCategories = new Set(["unknown_incoming", "unknown_outgoing"]);

const CategoryTable = ({ title, rows }) => (
    <div className="pr-tbl-wrap">
        <table className="pr-tbl">
            <thead>
                <tr>
                    <th>{title}</th>
                    <th>Кол-во</th>
                    <th className="num">Сумма</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={`${row.category}-${row.mapping_status || ""}`}>
                        <td>
                            {unknownCategories.has(row.category)
                                ? <span className="pr-pill r">{categoryLabels[row.category] || row.category}</span>
                                : (categoryLabels[row.category] || row.category || "—")}
                        </td>
                        <td>{row.count}</td>
                        <td className="num">{formatAmount(row.amount)}</td>
                    </tr>
                ))}
                {rows.length === 0 && (
                    <tr className="pr-empty-row"><td colSpan={3}>Нет данных за период.</td></tr>
                )}
            </tbody>
        </table>
    </div>
);

const novapayStatusTone = {
    uploaded: "off",
    parsed: "off",
    needs_review: "r",
    confirmed: "blue",
    exported: "g",
    cancelled: "off",
};

const NovaPayImportPanel = () => {
    const [organizations, setOrganizations] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [payerOrganizationId, setPayerOrganizationId] = useState("");
    const [payerAccountId, setPayerAccountId] = useState("");
    const [files, setFiles] = useState([]);
    const [draftEdits, setDraftEdits] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [uploadProgress, setUploadProgress] = useState("");
    const [expandedWarningIds, setExpandedWarningIds] = useState(() => new Set());

    const toggleWarningExpanded = (draftId) => {
        setExpandedWarningIds((prev) => {
            const next = new Set(prev);
            if (next.has(draftId)) next.delete(draftId);
            else next.add(draftId);
            return next;
        });
    };

    const selectedOrganization = useMemo(
        () => organizations.find((item) => String(item.id) === String(payerOrganizationId)) || null,
        [organizations, payerOrganizationId],
    );
    const payerAccounts = useMemo(() => selectedOrganization?.accounts || [], [selectedOrganization]);
    const selectedAccount = payerAccounts.find((item) => String(item.id) === String(payerAccountId)) || null;

    const findDefaultPayerOrganization = (rows) => (
        (rows || []).find((org) => String(org.tax_id || "") === "3655203299")
        || (rows || []).find((org) => String(org.short_name || org.name || "").toLowerCase().includes("цимко"))
        || (rows || [])[0]
        || null
    );

    const findDefaultPayerAccount = (accounts) => (
        (accounts || []).find((account) => String(account.account_number || "").replace(/\s/g, "") === "UA909358710000067329000099845")
        || (accounts || []).find((account) => String(account.label || account.account_title || "").toLowerCase().includes("novapay"))
        || (accounts || [])[0]
        || null
    );

    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            const [orgRows, draftRows] = await Promise.all([
                getBusinessOrganizations({ is_active: true }),
                getNovaPayDrafts({ supplierCode: "D7", limit: 200 }),
            ]);
            setOrganizations(orgRows || []);
            setDrafts(draftRows || []);
            if (!payerOrganizationId && orgRows?.length) {
                const defaultOrg = findDefaultPayerOrganization(orgRows);
                setPayerOrganizationId(String(defaultOrg.id));
                const defaultAccount = findDefaultPayerAccount(defaultOrg.accounts || []);
                if (defaultAccount) {
                    setPayerAccountId(String(defaultAccount.id));
                }
            }
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось загрузить NovaPay импорт.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!payerAccounts.length) {
            setPayerAccountId("");
            return;
        }
        if (!payerAccounts.some((account) => String(account.id) === String(payerAccountId))) {
            setPayerAccountId(String(findDefaultPayerAccount(payerAccounts).id));
        }
    }, [payerAccounts, payerAccountId]);

    const draftValue = (draft, field) => {
        const edit = draftEdits[draft.id] || {};
        if (Object.prototype.hasOwnProperty.call(edit, field)) {
            return edit[field] ?? "";
        }
        return draft[field] ?? "";
    };

    const updateEdit = (draftId, field, value) => {
        setDraftEdits((prev) => ({
            ...prev,
            [draftId]: {
                ...(prev[draftId] || {}),
                [field]: value,
            },
        }));
    };

    const payerPayload = () => ({
        payer_business_entity_id: payerOrganizationId ? Number(payerOrganizationId) : null,
        payer_business_account_id: payerAccountId ? Number(payerAccountId) : null,
        payer_tax_id: selectedOrganization?.tax_id || "",
        payer_iban: selectedAccount?.account_number || "",
    });

    const uploadFiles = async () => {
        if (!files.length) {
            setError("Выберите один или несколько PDF счетов.");
            return;
        }
        setLoading(true);
        setError("");
        setMessage("");
        setUploadProgress("");
        try {
            const uploadedItems = [];
            const payload = payerPayload();
            for (const [index, file] of files.entries()) {
                setUploadProgress(`Загрузка файла ${index + 1} из ${files.length}: ${file.name}`);
                const item = await uploadPediakidInvoice(file);
                uploadedItems.push(item);
                if (payload.payer_business_entity_id && payload.payer_business_account_id) {
                    await updateNovaPayDraft(item.draft.id, payload);
                }
            }
            setFiles([]);
            setMessage(`Загружено файлов: ${uploadedItems.length}.`);
            await loadData();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось загрузить PDF файлы.");
        } finally {
            setUploadProgress("");
            setLoading(false);
        }
    };

    const saveDraft = async (draft) => {
        if (!window.confirm(`Удалить черновик #${draft.id}?`)) {
            return;
        }
        setLoading(true);
        setError("");
        setMessage("");
        try {
            await updateNovaPayDraft(draft.id, {
                ...payerPayload(),
                ...(draftEdits[draft.id] || {}),
            });
            setDraftEdits((prev) => {
                const next = { ...prev };
                delete next[draft.id];
                return next;
            });
            setMessage(`Черновик #${draft.id} сохранен.`);
            await loadData();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось сохранить черновик.");
        } finally {
            setLoading(false);
        }
    };

    const deleteDraft = async (draft) => {
        setLoading(true);
        setError("");
        setMessage("");
        try {
            await deleteNovaPayDraft(draft.id);
            setSelectedIds((prev) => prev.filter((item) => item !== draft.id));
            setDraftEdits((prev) => {
                const next = { ...prev };
                delete next[draft.id];
                return next;
            });
            setMessage(`Черновик #${draft.id} удален.`);
            await loadData();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось удалить черновик.");
        } finally {
            setLoading(false);
        }
    };

    const clearAllDrafts = async () => {
        if (!drafts.length) {
            setError("Нет платежей для очистки.");
            return;
        }
        if (!window.confirm(`Удалить все платежи NovaPay из текущего списка (${drafts.length})?`)) {
            return;
        }
        setLoading(true);
        setError("");
        setMessage("");
        setUploadProgress("");
        try {
            for (const [index, draft] of drafts.entries()) {
                setUploadProgress(`Удаление платежа ${index + 1} из ${drafts.length}: #${draft.id}`);
                await deleteNovaPayDraft(draft.id);
            }
            setSelectedIds([]);
            setDraftEdits({});
            setMessage(`Удалено платежей: ${drafts.length}.`);
            await loadData();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось очистить платежи NovaPay.");
        } finally {
            setUploadProgress("");
            setLoading(false);
        }
    };

    const toggleSelected = (draftId) => {
        setSelectedIds((prev) => (
            prev.includes(draftId) ? prev.filter((item) => item !== draftId) : [...prev, draftId]
        ));
    };

    const exportSelected = async () => {
        const targetIds = selectedIds.length
            ? selectedIds
            : drafts.filter((draft) => draft.status !== "exported" && draft.status !== "cancelled").map((draft) => draft.id);
        if (!targetIds.length) {
            setError("Нет черновиков для экспорта.");
            return;
        }
        setLoading(true);
        setError("");
        setMessage("");
        try {
            for (const draftId of targetIds) {
                const draft = drafts.find((item) => item.id === draftId);
                if (draft?.status !== "confirmed") {
                    await confirmNovaPayDraft(draftId, {
                        ...payerPayload(),
                        ...(draftEdits[draftId] || {}),
                    });
                }
            }
            const exportResult = await exportNovaPayDrafts(targetIds);
            const response = await downloadNovaPayExport(exportResult.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", exportResult.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setSelectedIds([]);
            setMessage(`Экспортирован файл ${exportResult.filename}.`);
            await loadData();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось экспортировать XLSX.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pr-card">
            <div className="pr-panel-head">
                <h3>NovaPay импорт Pediakid</h3>
                <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" onClick={loadData} disabled={loading}>Обновить</button>
            </div>
            <div className="pr-panel-pad stack">
                {(loading || error || message) && (
                    <div className={`pr-note${error ? " err" : ""}`}>
                        <span className="dot" />
                        {loading ? "Загрузка..." : error || message}
                    </div>
                )}
                <div className="pr-form-grid">
                    <div className="pr-fld">
                        <label>Юрлицо плательщика</label>
                        <select className="pr-inp" value={payerOrganizationId} onChange={(event) => setPayerOrganizationId(event.target.value)}>
                            {organizations.map((org) => (
                                <option key={org.id} value={org.id}>{org.short_name} · {org.tax_id || "без кода"}</option>
                            ))}
                        </select>
                    </div>
                    <div className="pr-fld">
                        <label>IBAN отправителя</label>
                        <select className="pr-inp" value={payerAccountId} onChange={(event) => setPayerAccountId(event.target.value)}>
                            {payerAccounts.map((account) => (
                                <option key={account.id} value={account.id}>{account.label || account.account_title || account.account_number} · {account.account_number}</option>
                            ))}
                        </select>
                    </div>
                    <div className="pr-fld">
                        <label>PDF счета Pediakid</label>
                        <input
                            className="pr-inp"
                            type="file"
                            accept="application/pdf,.pdf"
                            multiple
                            onChange={(event) => setFiles(Array.from(event.target.files || []))}
                        />
                    </div>
                </div>
                {!!files.length && (
                    <div className="pr-mut">
                        Выбрано файлов: {files.length}
                    </div>
                )}
                {!!uploadProgress && (
                    <div className="pr-mut">
                        {uploadProgress}
                    </div>
                )}
                <div className="pr-actions-row">
                    <button type="button" className="pr-btn pr-btn-primary" onClick={uploadFiles} disabled={loading || !files.length}>Загрузить файлы</button>
                    <button type="button" className="pr-btn pr-btn-primary" onClick={exportSelected} disabled={loading || !drafts.some((draft) => draft.status !== "exported" && draft.status !== "cancelled")}>Экспорт XLSX</button>
                    <button type="button" className="pr-btn pr-btn-danger" onClick={clearAllDrafts} disabled={loading || !drafts.length}>Очистить все</button>
                </div>
                <div className="pr-tbl-wrap">
                    <table className="pr-tbl">
                        <thead>
                            <tr>
                                <th>Export</th>
                                <th>Статус</th>
                                <th>Счет</th>
                                <th>Получатель</th>
                                <th>IBAN</th>
                                <th>Сумма</th>
                                <th>Назначение</th>
                                <th>Warnings</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drafts.map((draft) => {
                                const warnings = draft.warnings || [];
                                const warningsExpanded = expandedWarningIds.has(draft.id);
                                const warningsText = warnings.join("; ");
                                return (
                                <tr key={draft.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(draft.id)}
                                            disabled={draft.status === "exported" || draft.status === "cancelled"}
                                            onChange={() => toggleSelected(draft.id)}
                                        />
                                    </td>
                                    <td><span className={`pr-pill ${novapayStatusTone[draft.status] || "off"}`}>{draft.status}</span></td>
                                    <td>
                                        <input className="pr-inp sm cell" style={{ display: "block", width: "150px" }} value={draftValue(draft, "invoice_number")} onChange={(event) => updateEdit(draft.id, "invoice_number", event.target.value)} />
                                        <input className="pr-inp sm cell" style={{ display: "block", width: "150px", marginTop: "4px" }} type="date" value={String(draftValue(draft, "invoice_date") || "").slice(0, 10)} onChange={(event) => updateEdit(draft.id, "invoice_date", event.target.value)} />
                                    </td>
                                    <td>
                                        <input className="pr-inp sm cell" style={{ display: "block", minWidth: "220px" }} value={draftValue(draft, "recipient_name")} onChange={(event) => updateEdit(draft.id, "recipient_name", event.target.value)} />
                                        <input className="pr-inp sm cell" style={{ display: "block", minWidth: "160px", marginTop: "4px" }} value={draftValue(draft, "recipient_tax_id")} onChange={(event) => updateEdit(draft.id, "recipient_tax_id", event.target.value)} />
                                    </td>
                                    <td>
                                        <input className="pr-inp sm mono cell" style={{ minWidth: "300px" }} value={draftValue(draft, "recipient_iban")} onChange={(event) => updateEdit(draft.id, "recipient_iban", event.target.value)} />
                                    </td>
                                    <td>
                                        <input className="pr-inp sm cell" style={{ width: "110px" }} value={draftValue(draft, "amount")} onChange={(event) => updateEdit(draft.id, "amount", event.target.value)} />
                                    </td>
                                    <td>
                                        <input className="pr-inp sm cell" style={{ minWidth: "260px" }} value={draftValue(draft, "purpose")} onChange={(event) => updateEdit(draft.id, "purpose", event.target.value)} />
                                    </td>
                                    <td>
                                        {warnings.length ? (
                                            <span
                                                className={`pr-warn-chip${warningsExpanded ? " expanded" : ""}`}
                                                onClick={() => toggleWarningExpanded(draft.id)}
                                                title={warningsExpanded ? "" : warningsText}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                {warningsText}
                                            </span>
                                        ) : <span className="pr-mut">—</span>}
                                    </td>
                                    <td>
                                        <div className="pr-actions-row">
                                            <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" onClick={() => saveDraft(draft)} disabled={loading || draft.status === "exported"}>Сохранить</button>
                                            <button type="button" className="pr-btn pr-btn-danger pr-btn-sm" onClick={() => deleteDraft(draft)} disabled={loading}>Удалить</button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                            {!drafts.length && (
                                <tr>
                                    <td colSpan={9} className="pr-mut">Нет черновиков NovaPay.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const EnterpriseFlowBreakdown = ({ title, rows, total, tone }) => (
    <div className="pr-tbl-wrap">
        <table className="pr-tbl">
            <thead><tr><th colSpan={2}>{title}</th></tr></thead>
            <tbody>
                {rows.map(({ label, amount }) => (
                    <tr key={label}>
                        <td>{label}</td>
                        <td className="num">{formatAmount(amount)}</td>
                    </tr>
                ))}
                <tr className="total">
                    <td>Итого {title.toLowerCase()}</td>
                    <td className={`num ${tone}`}>{formatAmount(total)}</td>
                </tr>
            </tbody>
        </table>
    </div>
);

const EnterpriseBreakdown = ({ rows, totals }) => {
    const incomingRows = (row) => [
        { label: "Клиентские", amount: row.customer },
        { label: "Агентские", amount: row.agency },
        { label: "Возвраты / прочие", amount: row.refundsOther },
        { label: "Исключенные", amount: row.excludedIncoming },
        { label: "Неизвестные", amount: row.unknownIncoming },
    ];
    const outgoingRows = (row) => [
        { label: "Поставщики", amount: row.suppliers },
        { label: "Площадки (Tabletki и др.)", amount: row.platforms },
        { label: "Налоги", amount: row.taxes },
        { label: "Личные перебросы", amount: row.owner },
        { label: "Логистика", amount: row.logistics },
        { label: "Телефония / связь", amount: row.telephony },
        { label: "Аренда помещения", amount: row.rent },
        { label: "Прочие расходы", amount: row.otherExpenses },
        { label: "Возвраты покупателям", amount: row.customerRefunds },
        { label: "Неизвестные", amount: row.unknownOutgoing },
    ];
    return (
        <div className="pr-view">
            <div className="pr-panel-head">
                <h3>Структура платежей по предприятиям</h3>
                <span className="pr-mut" style={{ fontSize: "12.5px" }}>{rows.length} предприятий</span>
            </div>
            {rows.length === 0 ? (
                <div className="pr-card pr-panel-pad pr-mut">За выбранный период данных нет.</div>
            ) : (
                <>
                    <div className="pr-card">
                        <div className="pr-panel-head"><h3>Итого по всем предприятиям</h3></div>
                        <div className="pr-panel-pad pr-split2">
                            <EnterpriseFlowBreakdown title="Входящие" rows={incomingRows(totals)} total={totals.incoming} tone="pr-good-text" />
                            <EnterpriseFlowBreakdown title="Исходящие" rows={outgoingRows(totals)} total={totals.outgoing} tone="pr-bad-text" />
                        </div>
                    </div>
                    {rows.map((row) => (
                        <div key={row.name} className="pr-card">
                            <div className="pr-panel-head"><h3>{row.name}</h3></div>
                            <div className="pr-panel-pad pr-split2">
                                <EnterpriseFlowBreakdown title="Входящие" rows={incomingRows(row)} total={row.incoming} tone="pr-good-text" />
                                <EnterpriseFlowBreakdown title="Исходящие" rows={outgoingRows(row)} total={row.outgoing} tone="pr-bad-text" />
                            </div>
                        </div>
                    ))}
                </>
            )}
            </div>
    );
};

const AccountsTable = ({ rows, drafts, setDrafts, onSave, onSaveOpening, loading, defaultBalanceDate, defaultPeriodMonth }) => {
    const updateDraft = (accountId, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [accountId]: {
                balance_date: rows.find((row) => row.account_id === accountId)?.balance_checkpoint_date || defaultBalanceDate,
                actual_balance: rows.find((row) => row.account_id === accountId)?.balance_checkpoint_amount || "",
                opening_period_month: defaultPeriodMonth,
                opening_balance: rows.find((row) => row.account_id === accountId)?.opening_balance || "0",
                comment: "",
                ...(prev[accountId] || {}),
                [field]: value,
            },
        }));
    };
    const draftValue = (row, field, fallback) => drafts[row.account_id]?.[field] ?? fallback ?? "";
    return (
    <div className="pr-card">
        <div className="pr-panel-head"><h3>Движение и контроль остатков по счетам</h3></div>
        <div className="pr-panel-pad pr-tbl-wrap">
            <table className="pr-tbl">
                <thead>
                    <tr>
                        <th>Предприятие / счёт</th>
                        <th className="num">Факт</th>
                        <th className="num">Начало</th>
                        <th className="num">Входящие</th>
                        <th className="num">Исходящие</th>
                        <th className="num">Конец расчёт</th>
                        <th>Контроль</th>
                        <th>Ввести факт</th>
                        <th>Начало месяца</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const hasWarning = row.balance_checkpoint_status === "warning";
                        return (
                        <tr key={row.account_id}>
                            <td>
                                {row.business_entity_name}
                                <div className="pr-sub-meta"><strong>{row.account_label || "—"}</strong> · {row.account_number}</div>
                            </td>
                            <td className="num">
                                {formatAmount(row.balance_checkpoint_amount)}
                                {row.balance_checkpoint_date && <div className="pr-sub-meta">на {row.balance_checkpoint_date}</div>}
                            </td>
                            <td className="num">{formatAmount(row.opening_balance)}</td>
                            <td className="num">{formatAmount(row.incoming?.amount)}</td>
                            <td className="num">{formatAmount(row.outcoming?.amount)}</td>
                            <td className="num">{formatAmount(row.calculated_closing_balance)}</td>
                            <td style={{ minWidth: "190px" }}>
                                {hasWarning ? (
                                    <span className="pr-pill bad">Расхождение {formatAmount(row.balance_checkpoint_difference)}</span>
                                ) : (
                                    <span className={`pr-pill ${row.balance_checkpoint_status === "ok" ? "g" : "r"}`}>
                                        {row.balance_checkpoint_status === "ok" ? "Совпадает" : "Нет предыдущего факта"}
                                    </span>
                                )}
                                {row.balance_checkpoint_calculated_amount && (
                                    <div className="pr-sub-meta">расчёт на дату: {formatAmount(row.balance_checkpoint_calculated_amount)}</div>
                                )}
                                <div className="pr-sub-meta">
                                    начало: {balanceSourceLabels[row.opening_balance_source] || "нет"}{row.opening_balance_source_period ? ` (${row.opening_balance_source_period.slice(0, 7)})` : ""}
                                </div>
                            </td>
                            <td style={{ minWidth: "300px" }}>
                                <div className="pr-rule-row compact">
                                    <input
                                        className="pr-inp sm"
                                        style={{ flex: "1 1 130px" }}
                                        type="date"
                                        value={draftValue(row, "balance_date", row.balance_checkpoint_date || defaultBalanceDate)}
                                        onChange={(event) => updateDraft(row.account_id, "balance_date", event.target.value)}
                                    />
                                    <input
                                        className="pr-inp sm mono"
                                        style={{ flex: "1 1 110px" }}
                                        type="number"
                                        step="0.01"
                                        placeholder="Факт остаток"
                                        value={draftValue(row, "actual_balance", row.balance_checkpoint_amount || "")}
                                        onChange={(event) => updateDraft(row.account_id, "actual_balance", event.target.value)}
                                    />
                                </div>
                                <div className="pr-rule-row compact" style={{ marginTop: "6px" }}>
                                    <input
                                        className="pr-inp sm"
                                        style={{ flex: "2 1 150px" }}
                                        placeholder="Комментарий"
                                        value={draftValue(row, "comment", "")}
                                        onChange={(event) => updateDraft(row.account_id, "comment", event.target.value)}
                                    />
                                    <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" disabled={loading} onClick={() => onSave(row.account_id)}>
                                        Зафиксировать
                                    </button>
                                </div>
                            </td>
                            <td style={{ minWidth: "300px" }}>
                                <div className="pr-rule-row compact">
                                    <input
                                        className="pr-inp sm"
                                        style={{ flex: "1 1 130px" }}
                                        type="date"
                                        value={draftValue(row, "opening_period_month", defaultPeriodMonth)}
                                        onChange={(event) => updateDraft(row.account_id, "opening_period_month", event.target.value)}
                                    />
                                    <input
                                        className="pr-inp sm mono"
                                        style={{ flex: "1 1 110px" }}
                                        type="number"
                                        step="0.01"
                                        placeholder="Начало"
                                        value={draftValue(row, "opening_balance", row.opening_balance || "0")}
                                        onChange={(event) => updateDraft(row.account_id, "opening_balance", event.target.value)}
                                    />
                                </div>
                                <div className="pr-rule-row compact" style={{ marginTop: "6px" }}>
                                    <input
                                        className="pr-inp sm"
                                        style={{ flex: "2 1 150px" }}
                                        placeholder="Комментарий"
                                        value={draftValue(row, "opening_comment", "")}
                                        onChange={(event) => updateDraft(row.account_id, "opening_comment", event.target.value)}
                                    />
                                    <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" disabled={loading} onClick={() => onSaveOpening(row.account_id)}>
                                        Задать
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );})}
                    {rows.length === 0 && (
                        <tr className="pr-empty-row"><td colSpan={9}>Нет счетов за период.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    </div>
    );
};

const MappingPanel = ({
    unmapped,
    mappings,
    incomingRules,
    outgoingRules,
    unknownIncomingPayments,
    unknownOutgoingPayments,
    suppliers,
    drafts,
    setDrafts,
    incomingDrafts,
    setIncomingDrafts,
    outgoingDrafts,
    setOutgoingDrafts,
    onCreate,
    onCreateIncomingRule,
    onToggleIncomingRule,
    onCreateOutgoingRule,
    onToggleOutgoingRule,
    onUpdateOutgoingRule,
    loading,
}) => {
    const [mtab, setMtab] = useState("in");
    const [editingOutgoingRuleId, setEditingOutgoingRuleId] = useState(null);
    const supplierOptions = [...suppliers].sort((left, right) => String(left.code).localeCompare(String(right.code)));
    const updateDraft = (key, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [key]: {
                match_type: "exact",
                field_scope: "counterparty_name",
                priority: "100",
                ...(prev[key] || {}),
                [field]: value,
            },
        }));
    };
    const updateIncomingDraft = (key, field, value) => {
        setIncomingDrafts((prev) => ({
            ...prev,
            [key]: {
                target_category: "customer_receipt",
                match_type: "contains",
                field_scope: "purpose",
                priority: "100",
                ...(prev[key] || {}),
                [field]: value,
            },
        }));
    };
    const updateOutgoingDraft = (key, field, value) => {
        setOutgoingDrafts((prev) => ({
            ...prev,
            [key]: {
                target_category: "owner_withdrawal",
                match_type: "contains",
                field_scope: "purpose",
                priority: "100",
                ...(prev[key] || {}),
                [field]: value,
            },
        }));
    };
    const draftValue = (key, field, fallback = "") => drafts[key]?.[field] ?? fallback;
    const incomingDraftValue = (key, field, fallback = "") => incomingDrafts[key]?.[field] ?? fallback;
    const outgoingDraftValue = (key, field, fallback = "") => outgoingDrafts[key]?.[field] ?? fallback;
    const renderOutgoingMatchControls = (key, source = {}) => {
        const matchType = outgoingDraftValue(key, "match_type", "contains");
        return (
        <>
            <select
                className="pr-inp"
                value={outgoingDraftValue(key, "target_category", "owner_withdrawal")}
                onChange={(event) => updateOutgoingDraft(key, "target_category", event.target.value)}
            >
                <option value="owner_withdrawal">Личные перебросы / вывод</option>
                <option value="tax_payment">Налоги</option>
                <option value="logistics_expense">Логистика</option>
                <option value="platform_fee">Площадки</option>
                <option value="telephony_expense">Телефония / связь</option>
                <option value="refund_to_customer">Возврат покупателю</option>
                <option value="rent_expense">Аренда помещения</option>
                <option value="other_expense">Прочие расходы</option>
            </select>
            <select className="pr-inp" value={matchType} onChange={(event) => {
                const nextType = event.target.value;
                updateOutgoingDraft(key, "match_type", nextType);
                if (nextType === "tax_id") updateOutgoingDraft(key, "field_scope", "tax_id");
                else if (outgoingDraftValue(key, "field_scope", "purpose") === "tax_id") updateOutgoingDraft(key, "field_scope", "purpose");
            }}>
                <option value="contains">contains</option>
                <option value="search_text_contains">search text</option>
                <option value="exact">exact</option>
                <option value="tax_id">tax id</option>
            </select>
            <select className="pr-inp" value={outgoingDraftValue(key, "field_scope", "purpose")} disabled={matchType === "tax_id"} onChange={(event) => updateOutgoingDraft(key, "field_scope", event.target.value)}>
                <option value="purpose">Назначение</option>
                <option value="counterparty_name">Контрагент</option>
                <option value="comment">Комментарий</option>
                <option value="search_text">Весь текст</option>
                <option value="tax_id">ЕДРПОУ</option>
            </select>
            <input
                className="pr-inp pattern"
                placeholder={matchType === "tax_id" ? "ЕДРПОУ" : "Паттерн"}
                value={matchType === "tax_id"
                    ? outgoingDraftValue(key, "counterparty_tax_id", source.counterparty_tax_id || "")
                    : outgoingDraftValue(key, "pattern", source.purpose || "")}
                onChange={(event) => updateOutgoingDraft(key, matchType === "tax_id" ? "counterparty_tax_id" : "pattern", event.target.value)}
            />
            <input
                className="pr-inp prio"
                type="number"
                placeholder="Приоритет"
                value={outgoingDraftValue(key, "priority", "100")}
                onChange={(event) => updateOutgoingDraft(key, "priority", event.target.value)}
            />
        </>
        );
    };
    const renderIncomingCategorySelect = (key) => (
        <select
            className="pr-inp"
            value={incomingDraftValue(key, "target_category", "customer_receipt")}
            onChange={(event) => updateIncomingDraft(key, "target_category", event.target.value)}
        >
            <option value="customer_receipt">Клиентские</option>
            <option value="taxable_agency_receipt">Агентские облагаемые</option>
            <option value="other_receipt">Прочие</option>
            <option value="excluded_receipt">Исключить</option>
        </select>
    );
    const renderIncomingMatchControls = (key, source = {}) => (
        <>
            {renderIncomingCategorySelect(key)}
            <select className="pr-inp" value={incomingDraftValue(key, "match_type", "contains")} onChange={(event) => updateIncomingDraft(key, "match_type", event.target.value)}>
                <option value="contains">contains</option>
                <option value="search_text_contains">search text</option>
                <option value="exact">exact</option>
                <option value="tax_id">tax id</option>
            </select>
            <select className="pr-inp" value={incomingDraftValue(key, "field_scope", "purpose")} onChange={(event) => updateIncomingDraft(key, "field_scope", event.target.value)}>
                <option value="purpose">Назначение</option>
                <option value="counterparty_name">Контрагент</option>
                <option value="comment">Комментарий</option>
                <option value="search_text">Весь текст</option>
                <option value="tax_id">ЕДРПОУ</option>
            </select>
            <input
                className="pr-inp pattern"
                placeholder="Паттерн"
                value={incomingDraftValue(key, "pattern", source.purpose || "")}
                onChange={(event) => updateIncomingDraft(key, "pattern", event.target.value)}
            />
            <input
                className="pr-inp prio"
                type="number"
                placeholder="Приоритет"
                value={incomingDraftValue(key, "priority", "100")}
                onChange={(event) => updateIncomingDraft(key, "priority", event.target.value)}
            />
        </>
    );
    const renderSupplierSelect = (key) => (
        <select
            className="pr-inp"
            value={draftValue(key, "supplier_code")}
            onChange={(event) => updateDraft(key, "supplier_code", event.target.value)}
        >
            <option value="">Отправитель</option>
            {supplierOptions.map((supplier) => (
                <option key={supplier.code} value={supplier.code}>
                    {supplier.code} · {supplier.name}
                </option>
            ))}
        </select>
    );

    const activeIncoming = incomingRules.filter((r) => r.is_active).length;
    const activeOutgoing = (outgoingRules || []).filter((r) => r.is_active).length;

    const mtabs = [
        ["in", "Входящие правила", incomingRules.length],
        ["out", "Исходящие правила", (outgoingRules || []).length],
        ["senders", "Отправители", unmapped.length],
        ["legacy", "Legacy", mappings.length],
    ];

    return (
        <div className="pr-card" style={{ padding: "14px 14px 0" }}>
            <div className="pr-tabbar" role="tablist">
                {mtabs.map(([key, label, count]) => (
                    <button
                        key={key}
                        type="button"
                        className={`pr-tab${mtab === key ? " active" : ""}`}
                        onClick={() => setMtab(key)}
                    >
                        {label} <span className="count">{count}</span>
                    </button>
                ))}
            </div>

            {/* ---- Входящие правила ---- */}
            {mtab === "in" && (
                <div className="pr-mpanel" style={{ padding: "20px 2px 2px" }}>
                    <div className="pr-callout">
                        <span className="ic">ⓘ</span>
                        <div>Правила разносят неопознанные <b>входящие</b> платежи по категориям на основе текста назначения, контрагента или ЕДРПОУ — без правки кода. Проверяются по приоритету (меньше = раньше).</div>
                    </div>

                    <div className="pr-card">
                        <div className="pr-panel-head"><h3>Новое правило</h3></div>
                        <div className="pr-panel-pad">
                            <div className="pr-rule-row">
                                {renderIncomingMatchControls("incoming-manual")}
                                <button type="button" className="pr-btn pr-btn-primary" disabled={loading} onClick={() => onCreateIncomingRule("incoming-manual", {})}>Создать</button>
                            </div>
                        </div>
                    </div>

                    {unknownIncomingPayments.length ? (
                        <div className="pr-card">
                            <div className="pr-panel-head">
                                <h3>Неопознанные входящие за период</h3>
                                <span className="pr-mut" style={{ fontSize: "12.5px" }}>{unknownIncomingPayments.length}</span>
                            </div>
                            <div className="pr-panel-pad pr-tbl-wrap">
                                <table className="pr-tbl">
                                    <thead>
                                        <tr><th>Дата</th><th>Контрагент</th><th>Назначение</th><th className="num">Сумма</th><th>Быстрое правило</th></tr>
                                    </thead>
                                    <tbody>
                                        {unknownIncomingPayments.slice(0, 10).map((payment) => {
                                            const key = `incoming-${payment.id}`;
                                            return (
                                                <tr key={key}>
                                                    <td>{String(payment.payment_date || "").slice(0, 16).replace("T", " ")}</td>
                                                    <td>{payment.counterparty_name || "—"}</td>
                                                    <td style={{ minWidth: "260px", maxWidth: "420px" }}>{payment.purpose || "—"}</td>
                                                    <td className="num">{formatAmount(payment.amount)}</td>
                                                    <td className="pr-quick-cell">
                                                        <div className="pr-rule-row compact">
                                                            {renderIncomingMatchControls(key, payment)}
                                                            <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" disabled={loading} onClick={() => onCreateIncomingRule(key, payment)}>Создать</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    <div className="pr-card">
                        <div className="pr-panel-head">
                            <h3>Активные правила</h3>
                            <span className="pr-mut" style={{ fontSize: "12.5px" }}>{incomingRules.length} правил · {activeIncoming} активных</span>
                        </div>
                        <div className="pr-panel-pad pr-tbl-wrap">
                            <table className="pr-tbl">
                                <thead>
                                    <tr><th>Приоритет</th><th>Категория</th><th>Тип</th><th>Поле</th><th>Паттерн</th><th>ЕДРПОУ</th><th>Статус</th><th>Действие</th></tr>
                                </thead>
                                <tbody>
                                    {incomingRules.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.priority}</td>
                                            <td>{categoryLabels[row.target_category] || row.target_category}</td>
                                            <td>{row.match_type}</td>
                                            <td>{row.field_scope}</td>
                                            <td>{row.pattern || "—"}</td>
                                            <td>{row.counterparty_tax_id || "—"}</td>
                                            <td><span className={`pr-pill ${row.is_active ? "g" : "off"}`}>{row.is_active ? "active" : "inactive"}</span></td>
                                            <td>
                                                <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" disabled={loading} onClick={() => onToggleIncomingRule(row)}>
                                                    {row.is_active ? "Отключить" : "Включить"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {incomingRules.length === 0 && (
                                        <tr className="pr-empty-row"><td colSpan={8}>Правил нет.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Исходящие правила ---- */}
            {mtab === "out" && (
                <div className="pr-mpanel" style={{ padding: "20px 2px 2px" }}>
                    <div className="pr-callout">
                        <span className="ic">ⓘ</span>
                        <div>Разносит неопознанные <b>исходящие</b> по тексту назначения/контрагента — например «На чорну картку» → <b>Личные перебросы / вывод</b> — без деплоя.</div>
                    </div>

                    <div className="pr-card">
                        <div className="pr-panel-head"><h3>Новое правило</h3></div>
                        <div className="pr-panel-pad">
                            <div className="pr-rule-row">
                                {renderOutgoingMatchControls("outgoing-manual")}
                                <button type="button" className="pr-btn pr-btn-primary" disabled={loading} onClick={() => onCreateOutgoingRule("outgoing-manual", {})}>Создать</button>
                            </div>
                        </div>
                    </div>

                    {(unknownOutgoingPayments || []).length ? (
                        <div className="pr-card">
                            <div className="pr-panel-head">
                                <h3>Неопознанные исходящие за период</h3>
                                <span className="pr-mut" style={{ fontSize: "12.5px" }}>{unknownOutgoingPayments.length}</span>
                            </div>
                            <div className="pr-panel-pad pr-tbl-wrap">
                                <table className="pr-tbl">
                                    <thead>
                                        <tr><th>Дата</th><th>Контрагент</th><th>Назначение</th><th className="num">Сумма</th><th>Быстрое правило</th></tr>
                                    </thead>
                                    <tbody>
                                        {unknownOutgoingPayments.slice(0, 10).map((payment) => {
                                            const key = `outgoing-${payment.id}`;
                                            return (
                                                <tr key={key}>
                                                    <td>{String(payment.payment_date || "").slice(0, 16).replace("T", " ")}</td>
                                                    <td>{payment.counterparty_name || "—"}</td>
                                                    <td style={{ minWidth: "260px", maxWidth: "420px" }}>{payment.purpose || "—"}</td>
                                                    <td className="num">{formatAmount(payment.amount)}</td>
                                                    <td className="pr-quick-cell">
                                                        <div className="pr-rule-row compact">
                                                            {renderOutgoingMatchControls(key, payment)}
                                                            <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" disabled={loading} onClick={() => onCreateOutgoingRule(key, payment)}>Создать</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    <div className="pr-card">
                        <div className="pr-panel-head">
                            <h3>Активные правила</h3>
                            <span className="pr-mut" style={{ fontSize: "12.5px" }}>{(outgoingRules || []).length} правил · {activeOutgoing} активных</span>
                        </div>
                        <div className="pr-panel-pad pr-tbl-wrap">
                            <table className="pr-tbl">
                                <thead>
                                    <tr><th>Приоритет</th><th>Категория</th><th>Тип</th><th>Поле</th><th>Паттерн</th><th>ЕДРПОУ</th><th>Статус</th><th>Действие</th></tr>
                                </thead>
                                <tbody>
                                    {(outgoingRules || []).map((row) => {
                                        const ruleKey = `outgoing-edit-${row.id}`;
                                        return (
                                            <React.Fragment key={row.id}>
                                                <tr>
                                                    <td>{row.priority}</td>
                                                    <td>{categoryLabels[row.target_category] || row.target_category}</td>
                                                    <td>{row.match_type}</td>
                                                    <td>{row.field_scope}</td>
                                                    <td>{row.pattern || "—"}</td>
                                                    <td>{row.counterparty_tax_id || "—"}</td>
                                                    <td><span className={`pr-pill ${row.is_active ? "g" : "off"}`}>{row.is_active ? "active" : "inactive"}</span></td>
                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" disabled={loading} onClick={() => {
                                                            if (editingOutgoingRuleId === row.id) {
                                                                setEditingOutgoingRuleId(null);
                                                                return;
                                                            }
                                                            setOutgoingDrafts((prev) => ({
                                                                ...prev,
                                                                [ruleKey]: {
                                                                    target_category: row.target_category,
                                                                    match_type: row.match_type,
                                                                    field_scope: row.field_scope,
                                                                    pattern: row.pattern || "",
                                                                    counterparty_tax_id: row.counterparty_tax_id || "",
                                                                    priority: String(row.priority ?? 100),
                                                                },
                                                            }));
                                                            setEditingOutgoingRuleId(row.id);
                                                        }}>
                                                            {editingOutgoingRuleId === row.id ? "Закрыть" : "Изменить"}
                                                        </button>{" "}
                                                        <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" disabled={loading} onClick={() => onToggleOutgoingRule(row)}>
                                                            {row.is_active ? "Отключить" : "Включить"}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {editingOutgoingRuleId === row.id && (
                                                    <tr>
                                                        <td colSpan={8}>
                                                            <div className="pr-rule-row compact">
                                                                {renderOutgoingMatchControls(ruleKey, row)}
                                                                <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" disabled={loading} onClick={async () => {
                                                                    const saved = await onUpdateOutgoingRule(row, ruleKey);
                                                                    if (saved) setEditingOutgoingRuleId(null);
                                                                }}>Сохранить</button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {(outgoingRules || []).length === 0 && (
                                        <tr className="pr-empty-row"><td colSpan={8}>Правил нет.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Отправители ---- */}
            {mtab === "senders" && (
                <div className="pr-mpanel" style={{ padding: "20px 2px 2px" }}>
                    <div className="pr-callout">
                        <span className="ic">ⓘ</span>
                        <div>Связывает контрагента (по названию/ЕДРПОУ) с кодом отправителя — так исходящие оплаты попадают в отчёт «Поставщики».</div>
                    </div>

                    <div className="pr-card">
                        <div className="pr-panel-head"><h3>Создать связь контрагента с отправителем</h3></div>
                        <div className="pr-panel-pad">
                            <div className="pr-rule-row">
                                {renderSupplierSelect("manual")}
                                <select className="pr-inp" value={draftValue("manual", "match_type", "exact")} onChange={(event) => updateDraft("manual", "match_type", event.target.value)}>
                                    <option value="exact">exact</option>
                                    <option value="contains">contains</option>
                                    <option value="search_text_contains">search text</option>
                                    <option value="tax_id">tax id</option>
                                </select>
                                <select className="pr-inp" value={draftValue("manual", "field_scope", "counterparty_name")} onChange={(event) => updateDraft("manual", "field_scope", event.target.value)}>
                                    <option value="counterparty_name">Плательщик/получатель</option>
                                    <option value="purpose">Назначение</option>
                                    <option value="comment">Комментарий</option>
                                    <option value="search_text">Весь текст</option>
                                    <option value="tax_id">ЕДРПОУ</option>
                                </select>
                                <input className="pr-inp pattern" placeholder="Паттерн или название" value={draftValue("manual", "counterparty_pattern")} onChange={(event) => updateDraft("manual", "counterparty_pattern", event.target.value)} />
                                <input className="pr-inp prio" type="number" placeholder="Приоритет" value={draftValue("manual", "priority", "100")} onChange={(event) => updateDraft("manual", "priority", event.target.value)} />
                                <button type="button" className="pr-btn pr-btn-primary" disabled={loading} onClick={() => onCreate("manual", {})}>Создать</button>
                            </div>
                        </div>
                    </div>

                    <div className="pr-card">
                        <div className="pr-panel-head">
                            <h3>Не сопоставлено за период</h3>
                            <span className="pr-mut" style={{ fontSize: "12.5px" }}>{unmapped.length} групп</span>
                        </div>
                        <div className="pr-panel-pad pr-tbl-wrap">
                            <table className="pr-tbl">
                                <thead>
                                    <tr><th>Контрагент</th><th>ЕДРПОУ</th><th>Кол-во</th><th className="num">Сумма</th><th>Пример</th><th>Мапинг</th></tr>
                                </thead>
                                <tbody>
                                    {unmapped.map((row, index) => {
                                        const key = `${row.counterparty_name || "empty"}-${row.counterparty_tax_id || "empty"}-${index}`;
                                        return (
                                            <tr key={key}>
                                                <td>{row.counterparty_name || "—"}</td>
                                                <td>{row.counterparty_tax_id || "—"}</td>
                                                <td>{row.count}</td>
                                                <td className="num">{formatAmount(row.amount)}</td>
                                                <td style={{ maxWidth: "300px" }}>{row.examples?.[0]?.purpose || "—"}</td>
                                                <td className="pr-quick-cell" style={{ minWidth: "460px" }}>
                                                    <div className="pr-rule-row compact">
                                                        {renderSupplierSelect(key)}
                                                        <select className="pr-inp" value={draftValue(key, "match_type", row.counterparty_tax_id ? "tax_id" : "exact")} onChange={(event) => updateDraft(key, "match_type", event.target.value)}>
                                                            <option value="tax_id">tax id</option>
                                                            <option value="exact">exact</option>
                                                            <option value="contains">contains</option>
                                                            <option value="search_text_contains">search text</option>
                                                        </select>
                                                        <input className="pr-inp prio" type="number" placeholder="Приоритет" value={draftValue(key, "priority", "100")} onChange={(event) => updateDraft(key, "priority", event.target.value)} />
                                                        <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" disabled={loading} onClick={() => onCreate(key, row)}>Связать</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {unmapped.length === 0 && (
                                        <tr className="pr-empty-row"><td colSpan={6}>Всё сопоставлено за период.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Legacy ---- */}
            {mtab === "legacy" && (
                <div className="pr-mpanel" style={{ padding: "20px 2px 2px" }}>
                    <div className="pr-callout muted">
                        <span className="ic">⚠</span>
                        <div>Устаревшие правила старого формата (<code>supplier_code</code> + <code>counterparty_pattern</code>). Новые связи создавайте на вкладке «Отправители» — здесь оставлено только для истории и аудита, редактирование отключено.</div>
                    </div>

                    <div className="pr-card">
                        <div className="pr-panel-head">
                            <h3>Legacy правила мапинга</h3>
                            <span className="pr-mut" style={{ fontSize: "12.5px" }}>{mappings.length} правил · read-only</span>
                        </div>
                        <div className="pr-panel-pad pr-tbl-wrap">
                            <table className="pr-tbl">
                                <thead>
                                    <tr><th>Приоритет</th><th>Отправитель</th><th>Тип</th><th>Поле</th><th>Паттерн</th><th>ЕДРПОУ</th><th>Статус</th></tr>
                                </thead>
                                <tbody>
                                    {mappings.map((row) => (
                                        <tr key={row.id} className="legacy">
                                            <td>{row.priority}</td>
                                            <td>
                                                <strong>{row.supplier_code}</strong>
                                                <div className="pr-sub-meta">{row.supplier_name || "—"}</div>
                                            </td>
                                            <td>{row.match_type}</td>
                                            <td>{row.field_scope}</td>
                                            <td>{row.counterparty_pattern || "—"}</td>
                                            <td>{row.counterparty_tax_id || "—"}</td>
                                            <td><span className={`pr-pill ${row.is_active ? "off" : "off"}`}>{row.is_active ? "устарело" : "выключено"}</span></td>
                                        </tr>
                                    ))}
                                    {mappings.length === 0 && (
                                        <tr className="pr-empty-row"><td colSpan={7}>Legacy правил нет.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mappingStatusTone = { mapped: "g", unmapped: "bad", manual_required: "r" };

const PaymentDetailsTable = ({ title, rows, totalAmount, unknownCount }) => (
    <div className="pr-card">
        <div className="pr-panel-head">
            <h3>{title}</h3>
            <div className="pr-stats" style={{ marginTop: 0 }}>
                <span className="pr-stat">Итого <span className="num">{formatAmount(totalAmount ?? sumAmount(rows))}</span></span>
                <span className="pr-stat zero">Платежей <span className="num">{rows.length}</span></span>
                {unknownCount ? <span className="pr-stat bad">Неопознано <span className="num">{unknownCount}</span></span> : null}
            </div>
        </div>
        <div className="pr-panel-pad pr-tbl-wrap">
            <table className="pr-tbl">
                <thead>
                    <tr>
                        <th>Дата</th>
                        <th>Юрлицо / счёт</th>
                        <th>Контрагент</th>
                        <th>Категория</th>
                        <th>Поставщик</th>
                        <th>Статус</th>
                        <th>Назначение</th>
                        <th className="num">Сумма</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((payment) => {
                        const isUnknown = payment.category === "unknown_incoming" || payment.category === "unknown_outgoing";
                        return (
                            <tr key={payment.id}>
                                <td>{String(payment.payment_date || "").slice(0, 16).replace("T", " ")}</td>
                                <td>
                                    {payment.business_entity_name || "—"}
                                    <div className="pr-sub-meta">{payment.account_label || payment.account_number || "—"}</div>
                                </td>
                                <td>{payment.counterparty_name || "—"}</td>
                                <td>
                                    {isUnknown
                                        ? <span className="pr-pill r">{categoryLabels[payment.category] || payment.category}</span>
                                        : (categoryLabels[payment.category] || payment.category || "—")}
                                    {payment.is_internal_transfer ? <div className="pr-sub-meta">Внутренний перевод</div> : null}
                                </td>
                                <td>
                                    {payment.supplier_code ? <strong>{payment.supplier_code}</strong> : (payment.platform_name || payment.platform_code || "—")}
                                    {payment.supplier_name ? <div className="pr-sub-meta">{payment.supplier_name}</div> : null}
                                    {!payment.supplier_code && payment.platform_code ? <div className="pr-sub-meta">площадка</div> : null}
                                </td>
                                <td>
                                    {payment.mapping_status
                                        ? <span className={`pr-pill ${mappingStatusTone[payment.mapping_status] || "off"}`}>{payment.mapping_status}</span>
                                        : "—"}
                                </td>
                                <td style={{ minWidth: "300px", maxWidth: "480px" }}>{payment.purpose || "—"}</td>
                                <td className="num">{formatAmount(payment.amount)}</td>
                            </tr>
                        );
                    })}
                    {rows.length === 0 ? (
                        <tr className="pr-empty-row"><td colSpan={8}>За выбранный период платежей нет.</td></tr>
                    ) : null}
                </tbody>
            </table>
        </div>
    </div>
);

const SupplierTable = ({ rows }) => (
    <div className="pr-card">
        <div className="pr-panel-head">
            <h3>Оплаты поставщикам</h3>
            <span className="pr-mut" style={{ fontSize: "12.5px" }}>Итого {formatAmount(sumAmount(rows))}</span>
        </div>
        <div className="pr-panel-pad pr-tbl-wrap">
            <table className="pr-tbl">
                <thead>
                    <tr>
                        <th>Предприятие</th>
                        <th>Код</th>
                        <th>Поставщик</th>
                        <th>Кол-во</th>
                        <th className="num">Сумма</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={`${row.business_entity_name}-${row.supplier_code}`}>
                            <td>{row.business_entity_name || "—"}</td>
                            <td><strong>{row.supplier_code}</strong></td>
                            <td>{row.supplier_name}</td>
                            <td>{row.count}</td>
                            <td className="num">{formatAmount(row.amount)}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr className="pr-empty-row"><td colSpan={5}>Нет оплат поставщикам за период.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

const entityTypeLabels = {
    fop: "ФОП",
    company: "Компания",
    individual: "Физлицо",
    other: "Прочее",
};

const verificationStatusLabels = {
    draft: "Черновик",
    needs_review: "Требует проверки",
    verified: "Проверено",
    archived: "В архиве",
};

const UnverifiedEntitiesTable = ({ rows }) => (
    <div className="pr-tbl-wrap">
        <table className="pr-tbl">
            <thead>
                <tr>
                    <th>Предприятие</th>
                    <th>Полное название</th>
                    <th>ИНН / ЕДРПОУ</th>
                    <th>Тип</th>
                    <th>Статус</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.id}>
                        <td>{row.short_name || "—"}</td>
                        <td>{row.full_name || "—"}</td>
                        <td>{row.tax_id || "—"}</td>
                        <td>{entityTypeLabels[row.entity_type] || row.entity_type || "—"}</td>
                        <td><span className="pr-pill r">{verificationStatusLabels[row.verification_status] || row.verification_status || "—"}</span></td>
                    </tr>
                ))}
                {rows.length === 0 ? (
                    <tr className="pr-empty-row"><td colSpan={5}>Непроверенных предприятий нет.</td></tr>
                ) : null}
            </tbody>
        </table>
    </div>
);

const QualityPanel = ({ quality, selectedKey, onSelect, detailRows, detailLoading }) => {
    const selectedLabel = selectedKey ? qualityLabels[selectedKey] || selectedKey : "";
    return (
        <>
            <div className="pr-card">
                <div className="pr-panel-head"><h3>Контроль качества данных</h3></div>
                <div className="pr-panel-pad">
                    <div className="pr-kpi-grid">
                        {Object.entries(quality || {}).map(([key, item]) => {
                            const hasIssue = Number(item.count || 0) > 0;
                            const isEntities = key === "unverified_entities";
                            return (
                                <KpiCard
                                    key={key}
                                    label={qualityLabels[key] || key}
                                    amount={isEntities ? undefined : item.amount}
                                    valueText={isEntities ? String(item.count ?? 0) : undefined}
                                    count={isEntities ? undefined : item.count}
                                    countLabel="платежей"
                                    tone={hasIssue ? "warn" : "good"}
                                    onClick={() => onSelect(key)}
                                    active={selectedKey === key}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
            {selectedKey ? (
                detailLoading ? (
                    <div className="pr-card"><div className="pr-panel-pad pr-mut">Загрузка…</div></div>
                ) : selectedKey === "unverified_entities" ? (
                    <div className="pr-card">
                        <div className="pr-panel-head">
                            <h3>Детализация: {selectedLabel}</h3>
                            <span className="pr-mut" style={{ fontSize: "12.5px" }}>{detailRows.length}</span>
                        </div>
                        <div className="pr-panel-pad"><UnverifiedEntitiesTable rows={detailRows} /></div>
                    </div>
                ) : (
                    <PaymentDetailsTable title={`Детализация: ${selectedLabel}`} rows={detailRows} totalAmount={sumAmount(detailRows)} />
                )
            ) : (
                <div className="pr-card"><div className="pr-panel-pad pr-mut">Нажмите на карточку, чтобы увидеть конкретные платежи или предприятия.</div></div>
            )}
        </>
    );
};

const importStatusTone = { success: "g", completed: "g", error: "bad", failed: "bad" };

const ImportsTable = ({ rows }) => (
    <div className="pr-card">
        <div className="pr-panel-head"><h3>История импортов</h3></div>
        <div className="pr-panel-pad pr-tbl-wrap">
            <table className="pr-tbl">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Статус</th>
                        <th>Период</th>
                        <th>Incoming</th>
                        <th>Outcoming</th>
                        <th>Created</th>
                        <th>Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id}>
                            <td>#{row.id}</td>
                            <td><span className={`pr-pill ${importStatusTone[row.status] || "off"}`}>{row.status}</span></td>
                            <td>{row.period_from?.slice(0, 10)} – {row.period_to?.slice(0, 10)}</td>
                            <td>{row.incoming_count}</td>
                            <td>{row.outcoming_count}</td>
                            <td>{row.created_count}</td>
                            <td>{row.updated_count}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr className="pr-empty-row"><td colSpan={7}>Импортов пока нет.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

export default PaymentReportsPage;
